import { useEffect, useRef, useState, useCallback } from 'react';
import { X, Download, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { cn, getResolution, getPreviewAspectClass, getEffectiveDuration } from '@/lib/utils';
import { VideoExporter } from '../lib/video-exporter';
import { ScrollRenderer, type RenderConfig } from '../lib/renderer';
import { loadGoogleFont } from '../lib/font-loader';
import { AudioEngine } from '../lib/audio-engine';
import type { AppConfig } from '../lib/types';

interface PreviewProps {
  config: AppConfig;
  isRecording: boolean;
  onFinishRecording: (blob: Blob) => void;
  onCancel: () => void;
}

export function Preview({ config, isRecording, onFinishRecording, onCancel }: PreviewProps) {
  const [progress, setProgress] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => { cancelledRef.current = true; };
  }, []);

  const handleExport = useCallback(async () => {
    setProgress(0);
    cancelledRef.current = false;

    try {
      await loadGoogleFont(config.fontName);

      const [w, h] = getResolution(config.aspectRatio);
      const fps = 30;

      // Calculate effective duration first
      const durationVal = getEffectiveDuration(config);
      const totalFrames = Math.max(1, Math.floor(durationVal * fps));

      // 1 — Audio Mixing
      let mixedAudio: AudioBuffer | null = null;
      if (config.audio.musicUrl || config.audio.voiceoverUrl) {
        try {
          const music = config.audio.musicUrl ? await AudioEngine.loadAudio(config.audio.musicUrl) : null;
          const vo = config.audio.voiceoverUrl ? await AudioEngine.loadAudio(config.audio.voiceoverUrl) : null;
          mixedAudio = await AudioEngine.mixAudio(vo, music, config.audio, durationVal);
        } catch (err) {
          console.error('Audio mixing failed:', err);
        }
      }

      const renderer = new ScrollRenderer(w, h);
      const renderConfig: RenderConfig = {
        width: w,
        height: h,
        text: config.text,
        duration: durationVal,
        textColor: config.textColor,
        backgroundColor: config.backgroundColor,
        fontName: config.fontName,
        fontSize: config.fontSize,
        textAlign: config.textAlign,
        lineHeight: config.lineHeight,
        textShadow: config.textShadow,
        blur: config.blur,
        vignette: config.vignette,
        renderMode: config.renderMode,
        segments: config.segments,
      };

      if (config.backgroundImage?.trim()) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = config.backgroundImage;
          await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = rej; });
          renderConfig.backgroundImage = img;
        } catch { /* ignore */ }
      }

      let bgVideo: HTMLVideoElement | null = null;
      if (config.backgroundVideoUrl?.trim()) {
        try {
          bgVideo = document.createElement('video');
          bgVideo.crossOrigin = 'anonymous';
          bgVideo.src = config.backgroundVideoUrl;
          bgVideo.muted = true;
          bgVideo.loop = true;
          bgVideo.playsInline = true;
          await new Promise<void>((res, rej) => {
            bgVideo!.oncanplay = () => res();
            bgVideo!.onerror = rej;
          });
          await bgVideo.play();
          renderConfig.backgroundVideo = bgVideo;
        } catch { bgVideo = null; }
      }

      renderer.prepareText(renderConfig);

      const exporter = new VideoExporter(w, h, fps);
      await exporter.init(mixedAudio);

      for (let i = 0; i < totalFrames; i++) {
        if (cancelledRef.current) break;

        const currentTime = i / fps;
        const frameCanvas = renderer.renderFrame(renderConfig, currentTime);

        await exporter.encodeFrame(frameCanvas, i);
        setProgress(Math.round(((i + 1) / totalFrames) * 100));

        if (i % 10 === 0) await new Promise(r => setTimeout(r, 0));
      }

      if (!cancelledRef.current) {
        const blob = await exporter.finalize();
        onFinishRecording(blob);
      }

      renderer.destroy();
      if (bgVideo) { bgVideo.pause(); bgVideo.src = ''; }
    } catch (e) {
      console.error('Export failed:', e);
    }
  }, [config, onFinishRecording]);

  const [audioDuration, setAudioDuration] = useState(0);

  useEffect(() => {
    if (!isRecording) return;

    const loadAudioDuration = async () => {
      try {
        const music = config.audio.musicUrl ? await AudioEngine.loadAudio(config.audio.musicUrl) : null;
        const vo = config.audio.voiceoverUrl ? await AudioEngine.loadAudio(config.audio.voiceoverUrl) : null;
        const dur = Math.max(music?.duration || 0, vo?.duration || 0);
        setAudioDuration(dur);
      } catch (e) {
        console.error('Failed to load audio for preview duration:', e);
      }
    };

    loadAudioDuration();
  }, [isRecording, config.audio.musicUrl, config.audio.voiceoverUrl]);

  useEffect(() => {
    if (isRecording) {
      handleExport();
    }
  }, [isRecording, handleExport]);

  if (!isRecording) return null;

  const [w, h] = getResolution(config.aspectRatio);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 p-4 backdrop-blur-xl animate-in fade-in duration-300">
      <div className="flex w-full max-w-2xl flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary/20 p-2 text-primary">
              <Download className="size-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Exporting Video</h2>
              <p className="text-sm text-white/50">Please do not close this tab during encoding.</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            className="rounded-full text-white/50 hover:bg-white/10 hover:text-white"
          >
            <X className="size-6" />
          </Button>
        </div>

        <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-black shadow-2xl">
          <div className={cn("size-full flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm", getPreviewAspectClass(config.aspectRatio))}>
            <div className="relative flex size-32 items-center justify-center">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 128 128">
                <circle cx="64" cy="64" r="60" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-white/10" />
                <circle
                  cx="64" cy="64" r="60"
                  stroke="currentColor" strokeWidth="8" fill="transparent"
                  strokeDasharray={2 * Math.PI * 60}
                  strokeDashoffset={2 * Math.PI * 60 * (1 - progress / 100)}
                  strokeLinecap="round"
                  className="text-primary transition-all duration-200"
                />
              </svg>
              <span className="absolute text-2xl font-bold text-white">{progress}%</span>
            </div>
            <p className="mt-6 font-medium text-white/70">Encoding MP4 Video…</p>
          </div>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-500">
            <AlertCircle className="size-4" />
            <span>Local MP4 encoding. This may take a few moments depending on video length.</span>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              className="border-white/10 text-white hover:bg-white/5 hover:text-white"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
